
import { AdminLayout } from '@/components/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@inertiajs/react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ArrowLeft,
  Mail,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  Edit,
  Trash2,
  User as UserIcon,
  Users,
  TrendingUp
} from 'lucide-react';
import { useState } from 'react';
import EditUserDialog from './EditUserDialog';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  role: 'mentor' | 'mentee';
  category: 'pre-health' | 'graduate' | 'fellowship';
  email_verified: boolean;
  admin_verified: boolean;
  admin_rejected: boolean;
  rejection_reason?: string;
  rejected_at?: string;
  created_at: string;
  admin_verified_at?: string;
  verified_by?: {
    name: string;
    email: string;
  };
}

interface Application {
  sections: Section[];
  completed_at: string;
  status: 'active' | 'archived';
}

interface Section {
  id: number;
  section_name: string;
  description?: string;
  questions: Question[];
}

interface Question {
  id: number;
  key: string;
  label: string;
  question_type: string;
  required: boolean;
  options?: Array<{ label: string; value: string }>;
  response_value: string | string[];
}

interface CurrentMatch {
  id: number;
  mentor_name: string;
  mentor_email: string;
  matched_at: string;
  days_matched: number;
}

interface ActiveMentee {
  id: number;
  mentee_name: string;
  mentee_email: string;
  matched_at: string;
  days_matched: number;
}

interface PastMatch {
  id: number;
  other_user_name: string;
  other_user_role: string;
  status: 'completed' | 'ended';
  matched_at: string;
  ended_at?: string;
  duration_days?: number;
}

interface Matches {
  current_match?: CurrentMatch;
  active_mentees: ActiveMentee[];
  past_matches: PastMatch[];
  total_mentees?: number;
}

interface Metrics {
  total_sessions: number;
  total_hours: number;
  sessions_this_month: number;
}

interface Session {
  id: number;
  session_date: string;
  duration_minutes: number;
  occurred: boolean;
  discussion_summary: string;
  mentee_todos?: string;
  next_session_date?: string;
  other_user_name: string;
  created_at: string;
}

interface Props {
  user: User;
  application?: Application;
  matches: Matches;
  metrics: Metrics;
  sessions: Session[];
}

const getOptionLabel = (question: Question, value: string) => {
  if (!question.options) return value;
  const option = question.options.find((opt) => opt.value === value);
  return option?.label || value;
};

export default function UserShow({ user, application, matches, metrics, sessions }: Props) {
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);

  const getStatusBadge = () => {
    if (user.admin_rejected) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1 w-fit">
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    }
    if (user.admin_verified && user.email_verified) {
      return (
        <Badge variant="default" className="flex items-center gap-1 w-fit">
          <CheckCircle2 className="h-3 w-3" />
          Verified
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
        <Clock className="w-3 h-3" />
        Pending
      </Badge>
    );
  };

  return (
    <AdminLayout title="User Profile">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-4">
                <Link href="/admin/users">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </Link>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {user.first_name} {user.last_name}
                  </h1>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-gray-600">{user.email_address}</p>
                    {user.email_verified && (
                      <Badge variant="outline" className="text-xs">
                        <Mail className="h-3 w-3 mr-1" />
                        Email Verified
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {getStatusBadge()}
                    <Badge variant="outline" className="capitalize">
                      {user.role}
                    </Badge>
                    <Badge variant="outline">
                      {user.category === 'pre-health'
                        ? 'Pre-Health'
                        : user.category === 'graduate'
                          ? 'Graduate'
                          : 'Fellowship'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedUserForEdit(user)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button variant="destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* User Information */}
              <Card className="shadow-sm">
                <CardHeader className="border-b">
                  <CardTitle className="text-lg font-medium">User Information</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                        First Name
                      </p>
                      <p className="text-gray-900">{user.first_name}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                        Last Name
                      </p>
                      <p className="text-gray-900">{user.last_name}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                      Email Address
                    </p>
                    <p className="text-gray-900">{user.email_address}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                        Role
                      </p>
                      <p className="text-gray-900 capitalize">{user.role}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                        Category
                      </p>
                      <p className="text-gray-900">
                        {user.category === 'pre-health'
                          ? 'Pre-Health'
                          : user.category === 'graduate'
                            ? 'Graduate'
                            : 'Fellowship'}
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                          Joined
                        </p>
                        <p className="text-gray-900">
                          {formatDistanceToNow(new Date(user.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>

                      {user.admin_verified_at && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                            Verified
                          </p>
                          <p className="text-gray-900">
                            {formatDistanceToNow(new Date(user.admin_verified_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {user.verified_by && (
                      <div className="mt-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                          Verified By
                        </p>
                        <p className="text-gray-900">{user.verified_by.name}</p>
                      </div>
                    )}

                    {user.admin_rejected && user.rejection_reason && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md">
                        <p className="text-xs uppercase tracking-wider text-red-600 font-medium mb-1">
                          Rejection Reason
                        </p>
                        <p className="text-sm text-red-900">{user.rejection_reason}</p>
                        {user.rejected_at && (
                          <p className="text-xs text-red-600 mt-2">
                            Rejected {formatDistanceToNow(new Date(user.rejected_at), { addSuffix: true })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Match Information */}
              <Card className="shadow-sm">
                <CardHeader className="border-b">
                  <CardTitle className="text-lg font-medium">
                    {user.role === 'mentor' ? 'Active Mentees' : 'Current Match'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="">
                  {/* For Mentees - Current Match */}
                  {user.role === 'mentee' && matches.current_match && (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">
                            {matches.current_match.mentor_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {matches.current_match.mentor_email}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Matched {matches.current_match.days_matched} days ago
                          </p>
                        </div>
                        <Badge variant="default">Active</Badge>
                      </div>
                    </div>
                  )}

                  {user.role === 'mentee' && !matches.current_match && (
                    <p className="text-gray-500 text-sm">No current mentor</p>
                  )}

                  {/* For Mentors - Active Mentees */}
                  {user.role === 'mentor' && matches.active_mentees.length > 0 && (
                    <div className="space-y-3">
                      {matches.active_mentees.map((mentee) => (
                        <div
                          key={mentee.id}
                          className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{mentee.mentee_name}</p>
                            <p className="text-sm text-gray-600">{mentee.mentee_email}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              Matched {mentee.days_matched} days ago
                            </p>
                          </div>
                          <Badge variant="default">Active</Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {user.role === 'mentor' && matches.active_mentees.length === 0 && (
                    <p className="text-gray-500 text-sm">No active mentees</p>
                  )}

                  {/* Past Matches */}
                  {matches.past_matches.length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Past Matches</h4>
                      <div className="space-y-2">
                        {matches.past_matches.map((match) => (
                          <div
                            key={match.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{match.other_user_name}</p>
                              <p className="text-xs text-gray-500">
                                {match.duration_days} days • {match.status}
                              </p>
                            </div>
                            <Badge variant="outline" className="capitalize">
                              {match.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sessions History */}
              {sessions.length > 0 && (
                <Card className="shadow-sm">
                  <CardHeader className="border-b bg-gray-50/50">
                    <CardTitle className="text-lg font-medium">
                      Recent Sessions
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Last {sessions.length} sessions
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className="p-4 bg-gray-50 rounded-lg space-y-3"
                        >
                          {/* Session Header */}
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                Session with {session.other_user_name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {format(new Date(session.session_date), 'MMMM d, yyyy')} •{' '}
                                {session.duration_minutes} minutes
                              </p>
                            </div>
                            <Badge variant={session.occurred ? 'default' : 'destructive'}>
                              {session.occurred ? 'Completed' : 'Cancelled'}
                            </Badge>
                          </div>

                          {/* Discussion Summary */}
                          {session.discussion_summary && (
                            <div>
                              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
                                Discussion Summary
                              </p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {session.discussion_summary}
                              </p>
                            </div>
                          )}

                          {/* Mentee TODOs */}
                          {session.mentee_todos && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                              <p className="text-xs uppercase tracking-wider text-yellow-700 font-medium mb-1">
                                Action Items for {user.role === 'mentor' ? session.other_user_name : 'Mentee'}
                              </p>
                              <p className="text-sm text-yellow-900 whitespace-pre-wrap">
                                {session.mentee_todos}
                              </p>
                            </div>
                          )}

                          {/* Next Session */}
                          {session.next_session_date && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span>
                                Next session: {format(new Date(session.next_session_date), 'MMMM d, yyyy')}
                              </span>
                            </div>
                          )}

                          {/* Logged timestamp */}
                          <p className="text-xs text-gray-400">
                            Logged {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Application */}
              {application && (
                <Card className="shadow-sm">
                  <CardHeader className="border-b">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg font-medium">Application</CardTitle>
                      <Badge variant={application.status === 'active' ? 'default' : 'secondary'}>
                        {application.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Submitted {formatDistanceToNow(new Date(application.completed_at), { addSuffix: true })}
                    </p>
                  </CardHeader>
                  <CardContent className="">
                    <div className="space-y-6">
                      {application.sections.map((section, sectionIndex) => (
                        <div key={section.id}>
                          <div className="mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                              <span className="text-primary text-sm font-normal">
                                Section {sectionIndex + 1}
                              </span>
                              {section.section_name}
                            </h3>
                            {section.description && (
                              <p className="text-sm text-gray-500 mt-1">{section.description}</p>
                            )}
                          </div>

                          <div className="space-y-4 ml-4">
                            {section.questions.map((question) => (
                              <div key={question.id}>
                                <div className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-700 mb-1">
                                      {question.label}
                                      {question.required && (
                                        <span className="text-red-500 ml-0.5 text-xs">*</span>
                                      )}
                                    </p>
                                    <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                      {question.response_value ? (
                                        Array.isArray(question.response_value) ? (
                                          <ul className="space-y-1">
                                            {question.response_value.map((item, idx) => (
                                              <li key={idx} className="flex items-center gap-2">
                                                <span className="text-gray-400">→</span>
                                                {getOptionLabel(question, item)}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : question.question_type === 'text' ? (
                                          <div className="whitespace-pre-wrap">
                                            {question.response_value}
                                          </div>
                                        ) : question.question_type === 'select' ||
                                          question.question_type === 'radio' ? (
                                          getOptionLabel(question, question.response_value)
                                        ) : (
                                          question.response_value
                                        )
                                      ) : (
                                        <span className="text-gray-400 italic">
                                          No response provided
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {sectionIndex < application.sections.length - 1 && (
                            <div className="border-b border-gray-200 mt-6" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Metrics */}
            <div className="space-y-6">
              {user.role === 'mentor' && (
                <Card className="shadow-sm">
                  <CardHeader className="border-b">
                    <CardTitle className="text-lg font-medium">Activity Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                          Total Sessions
                        </span>
                        <TrendingUp className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-3xl font-light">{metrics.total_sessions}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary/60 rounded-full mt-2" />
                    </div>

                    <div className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                          Total Hours
                        </span>
                        <Clock className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-3xl font-light">{metrics.total_hours.toFixed(1)}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary/60 rounded-full mt-2" />
                    </div>

                    <div className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                          This Month
                        </span>
                        <Calendar className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-3xl font-light">{metrics.sessions_this_month}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary/60 rounded-full mt-2" />
                    </div>

                    {matches.total_mentees !== undefined && (
                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                            Total Impact
                          </span>
                          <Users className="h-4 w-4 text-gray-400" />
                        </div>
                        <p className="text-3xl font-light">{matches.total_mentees}</p>
                        <p className="text-xs text-gray-500 mt-1">All-time mentees</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedUserForEdit && (
        <EditUserDialog
          user={selectedUserForEdit}
          open={!!selectedUserForEdit}
          onOpenChange={(open) => !open && setSelectedUserForEdit(null)}
        />
      )}
    </AdminLayout>
  );
}
